import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import Bacon, { Observable } from 'baconjs'
import * as R from 'ramda'

import reqUtil from 'utils/request'
import resUtil from 'utils/resources'
import client from 'api/auth'

interface FeedbackAPI {
  getSessionAverage: () => number | null
  getReview: () => string | null
  getQuestionsRatings: () => SubjectRating[]
  getItemRatings: () => SubjectRating[]
  hasFeedback: () => boolean
}

const FeedbackCtx = createContext<FeedbackAPI | null>(null)
export interface Rating {
  id: string
  subject_id: string
  score: number
  comment: string | null
  created: number
  links: ResourceLink[]
  session_id: string
  status: 'open' | 'closed'
  updated: number
}
export interface Subject {
  id: string
  type: 'question' | 'item' | 'review'
  average: number
  description: string
  links: ResourceLink[]
  locale: string
  max: number
  min: string
  name: string
  rates: number
  created: number
  updated: number
}
export interface Session {
  id: string
  name: string
  average: number | null
  closed: boolean
  links: ResourceLink[]
  created: number
  updated: number
}

export type SubjectRating = Subject & Rating

interface ResourceLink {
  name: string
  methods: string[]
  url: string
}

async function loadFeedback(orderId: string) {
  const resSessions = await reqUtil.wrapRequest(client.api('feedbackSession', 'get', { name: orderId }))
  if (resSessions.sessions.length > 0) {
    const session = resSessions.sessions[0]
    const subjectsUrl = resUtil.findResourceLink(session, 'subjects', 'get')
    const ratingsUrl = resUtil.findResourceLink(session, 'ratings', 'get')
    const resSubjects = await reqUtil.wrapRequest(client.api(subjectsUrl, 'get'))
    const resRatings = await reqUtil.wrapRequest(client.api(ratingsUrl, 'get'))
    return { session, subjects: resSubjects.subjects, ratings: resRatings.ratings }
  } else return { session: null, subjects: [], ratings: [] }
}

export function FeedbackSvc(props: PropsWithChildren<{ orderId: string }>): JSX.Element {
  const [session, setSession] = useState<Session | null>(null)
  const [ratings, setRatings] = useState<Rating[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])

  useEffect(() => {
    loadFeedback(props.orderId).then((streams) => {
      setSession(streams.session)
      setRatings(streams.ratings)
      setSubjects(streams.subjects)
    })
  }, [props.orderId])

  const subjectsRatings = useMemo<SubjectRating[]>(
    () =>
      ratings.length > 0 && subjects.length > 0
        ? ratings.reduce((all, r) => {
            const q = subjects.find((q: Subject) => q.id == r.subject_id)
            return q ? [...all, { ...q, ...r }] : all
          }, [])
        : [],
    [subjects, ratings]
  )

  const api = {
    getSessionAverage: (): number | null => {
      return session ? session.average : null
    },
    getReview: (): string | null => {
      const r = subjectsRatings.find((f) => f.type == 'review')
      return r ? r.comment : null
    },
    getQuestionsRatings: (): SubjectRating[] => subjectsRatings.filter((f) => f.type == 'question'),
    getItemRatings: (): SubjectRating[] => subjectsRatings.filter((f) => f.type === 'item'),
    hasFeedback: (): boolean => !!session && subjectsRatings.length > 0,
  }

  return (
    <FeedbackCtx.Provider key={props.orderId} value={api}>
      {props.children}
    </FeedbackCtx.Provider>
  )
}

export function useFeedbackAPI(): FeedbackAPI {
  const api = useContext(FeedbackCtx)
  return api as FeedbackAPI
}
